import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { createMergeGridOptions } from '@app/Shared/grid/data-explorer-grid-options';
import { ToolsService } from '@app/Services/Utilities/tools.service';
import { iParsedEntity } from '@app/Shared/interfaces/iEntityParsed';
import { pluralize } from '@app/Shared/util/stringManipulation';
import { AppState, DashboardActions } from '@app/Store';
import { dashboardFeatureSelectors } from '@app/Store/features/dashboard.feature';
import { select, Store } from '@ngrx/store';
import { CheckboxSelectionCallbackParams, GridApi, GridOptions, GridReadyEvent, RowSelectedEvent } from 'ag-grid-community';
import { combineLatest, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'app-merge-section',
    templateUrl: './merge-section.component.html',
    styleUrls: ['./merge-section.component.scss'],
})
export class MergeSectionComponent implements OnInit, OnDestroy {
    public mergeGridOptions: GridOptions;
    public mergeData: any[] = [];
    public masterInfo: any = { masterTable: null, masterKey: null, mergeInfo: null };
    public selectedEntity: iParsedEntity | null = null;
    public noRecordSelected: boolean = true;
    public isExpanded: boolean = true;

    private mergeGridApi!: GridApi;
    private subscription: Subscription = new Subscription();

    constructor(private store: Store<AppState>, private cdr: ChangeDetectorRef, private toolsService: ToolsService) {
        this.mergeGridOptions = createMergeGridOptions(this.isFirstColumn);
    }
    ngOnInit(): void {
        // Subscribe to selected entity and selected records
        this.subscription.add(
            combineLatest(
                this.store.pipe(select(dashboardFeatureSelectors.selectSelectedEntity)).pipe(distinctUntilChanged((prev, curr) => prev?.entityName === curr?.entityName)),
                this.store.pipe(select(dashboardFeatureSelectors.selectSelectedRecords)).pipe(
                    distinctUntilChanged((prev, curr) => {
                        if (prev?.length !== curr?.length) return false;
                        if (prev?.length === 0 && curr?.length === 0) return true;
                        // Compare the first record's data reference
                        return prev?.[0]?.data === curr?.[0]?.data;
                    })
                )
            )
                .pipe(
                    distinctUntilChanged((prev, curr) => {
                        const [prevEntity, prevRecords] = prev;
                        const [currEntity, currRecords] = curr;
                        const entityChanged = prevEntity?.entityName !== currEntity?.entityName;
                        if (entityChanged) return false;
                        if (prevRecords?.length !== currRecords?.length) return false;
                        if (prevRecords?.length === 0 && currRecords?.length === 0) return true;
                        const prevKey = prevRecords?.[0]?.data?.[prevEntity?.key];
                        const currKey = currRecords?.[0]?.data?.[currEntity?.key];
                        return prevKey === currKey;
                    })
                )
                .subscribe(([entity, selectedRecords]) => {
                    if (entity && selectedRecords && selectedRecords.length > 0) {
                        this.selectedEntity = entity;
                        const selectedRow = selectedRecords[0].data;
                        const masterKey = selectedRow[entity.key];
                        this.masterInfo.masterTable = pluralize(entity.entityName);
                        this.masterInfo.masterKey = masterKey;
                        this.noRecordSelected = false;
                        this.fetchMergeData(entity, masterKey);
                    } else {
                        this.noRecordSelected = true;
                        this.selectedEntity = null;
                        this.masterInfo = { masterTable: null, masterKey: null, mergeInfo: null };
                        this.mergeData = [];
                        this.updateMergeGridData();
                    }
                })
        );

        // Subscribe to merge data
        this.subscription.add(
            this.store
                .pipe(select(dashboardFeatureSelectors.selectMergeData))
                .pipe(debounceTime(100))
                .subscribe((mergeData) => {
                    this.handleMergeData(mergeData);
                })
        );

        // Subscribe to merge grid selected row
        this.subscription.add(
            this.store.pipe(select(dashboardFeatureSelectors.selectMergeGridCurrentRowData)).subscribe((currentRow) => {
                if (currentRow == null) {
                    this.masterInfo.mergeInfo = null;
                } else {
                    this.masterInfo.mergeInfo = {
                        historyKey: currentRow.HistoryKey,
                        currentValue: currentRow.CurrentValue,
                    };
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
    private fetchMergeData(entity: iParsedEntity, masterKey: string): void {
        const masterTable = pluralize(entity.entityName);
        this.store.dispatch(DashboardActions.fetchMergeData({ masterTable, masterKey }));
    }

    private handleMergeData(mergeData: any) {
        const fillData = (value: any) => {
            if (value == null || (Array.isArray(value) && value.length == 0)) {
                return [];
            } else {
                const mappedData = value.results.reduce((acc: any, result: any) => {
                    const dataPropName = this.toolsService.getPropNameCaseSensitive(result, 'data');
                    if (result[dataPropName] && Array.isArray(result[dataPropName])) {
                        acc = acc.concat(result[dataPropName]);
                    }
                    return acc;
                }, []);
                return mappedData;
            }
        };

        this.mergeData = fillData(mergeData);
        this.updateMergeGridData();
    }

    public onMergeGridReady = (event: GridReadyEvent): void => {
        this.mergeGridApi = event.api;
        this.updateMergeGridData();
    };

    public onMergeRowSelect = (event: RowSelectedEvent): void => {
        const selectedRows = event.api.getSelectedRows();
        this.store.dispatch(DashboardActions.changeMergeGridSelectedData({ selectedRow: selectedRows[0] }));
    };
    private updateMergeGridData(): void {
        if (this.mergeGridApi) {
            this.mergeData = this.toolsService.reOrderArrayBasedOnPropName(this.mergeData, ['UILabel', 'CurrentValue', 'Survivorship']);
            this.updateGridData(this.mergeGridApi, this.mergeData, 'Merge Data');
        }
        this.cdr.detectChanges();
    }

    private updateGridData(gridApi: GridApi, data: any[], dataType: string): void {
        if (data.length > 0) {
            const keys = Object.keys(data[0]);
            const columnDefs = keys.map((key, index) => {
                // Pin first 1-2 columns (UILabel, CurrentValue, etc.)
                const pinned: 'left' | 'right' | null = index < 2 ? 'left' : null;
                return {
                    field: key,
                    headerName: key,
                    sortable: true,
                    filter: true,
                    flex: 1,
                    minWidth: 100,
                    pinned: pinned,
                    resizable: true,
                };
            });
            gridApi.setColumnDefs(columnDefs);
            gridApi.setRowData(data);
        } else {
            gridApi.setColumnDefs([]);
            gridApi.setRowData([]);
        }
    }
    private isFirstColumn = (params: CheckboxSelectionCallbackParams): boolean => {
        const displayedColumns = params?.columnApi?.getAllDisplayedColumns() || [];
        return displayedColumns[0] === params.column;
    };
}
